import { useSelector } from 'react-redux';

/**
 * Role ids as they appear in the role_master table and, through the permission
 * row, on RoleReducer.info.role_id.
 *
 * Only the ids this app actually branches on are named here; the rest are
 * still compared inline where they are used.
 */
export const ADMIN_ROLE_ID = 1; // ADMIN -- the MOHUA admin (mohuaadmin@gmail.com)
export const HELPDESK_ROLE_ID = 16; // HELPDESK -- read-only clone of the above
export const VIEWER_ADMIN_ROLE_ID = 17; // VIEWER_ADMIN -- same permission row as HELPDESK

/**
 * Roles that see the whole country rather than one jurisdiction.
 *
 * A dozen report and list screens used to spell this as `role_id === 1`, which
 * silently scoped any new national role down to "no filters matched" and left
 * it staring at an empty table. Route those checks through here instead.
 */
export const isNationalScopeRole = (roleId) => {
  const id = Number(roleId);
  return id === ADMIN_ROLE_ID || id === HELPDESK_ROLE_ID || id === VIEWER_ADMIN_ROLE_ID;
};

/**
 * Roles that may look at everything but must not write anything.
 *
 * HELPDESK (and VIEWER_ADMIN, which copies its permission row) reaches the
 * same pages as the MOHUA admin and reads the same data,
 * but cannot create an event, close one, file a report or add a user. Its
 * permission row already carries access.create/update/delete = false, which is
 * enough for the three screens that read `access` (users, roles, permissions);
 * every other submit button is gated on this helper instead, because those
 * screens never looked at `access` at all.
 *
 * UI only, like the rest of the role gates in this app -- the routes stay
 * reachable by URL. The backend is the security boundary (deleteEventV2 and
 * deleteUserV2 already hard-require role_id 1).
 */
export const isReadOnlyRole = (roleId) => {
  const id = Number(roleId);
  return id === HELPDESK_ROLE_ID || id === VIEWER_ADMIN_ROLE_ID;
};

/**
 * SHS-2026 is closed for data entry (client instruction, 21 Oct 2026, from
 * 2 PM). While true, every role -- the admin included -- gets view-only
 * access: no adding, editing, closing or reporting events, no daily updates,
 * no action plan or HGT-HGS uploads, no new users. Lists, reports and saved
 * entries stay viewable. Set back to false to reopen.
 *
 * UI only, like the role gates above: the API still accepts writes.
 */
export const SHS_2026_CLOSED = true;

export const CAMPAIGN_CLOSED_NOTICE =
  'Swachhata Hi Seva 2026 is closed. The portal is now view-only: entries can no longer be added, edited or submitted.';

/**
 * CTU and Clean Public Spaces reopened for adding, editing, closing and daily
 * updates while the rest of SHS-2026 stays closed (Director's instruction,
 * 22 Oct 2026). Covers Add/Edit/Close CTU, the daily update, Add/Edit/Close
 * CPS and the quick-edit dialogs on both status lists. Admin event delete stays
 * off. Set to false to close CTU and CPS again with everything else.
 */
export const CTU_CPS_OPEN = true;

/** Banner on every logged-in page while SHS-2026 is closed. */
export const CAMPAIGN_CLOSED_BANNER = CTU_CPS_OPEN
  ? 'Swachhata Hi Seva 2026 is closed. The portal is view-only, except CTU and Clean Public Spaces, which remain open for adding and editing.'
  : CAMPAIGN_CLOSED_NOTICE;

/**
 * isReadOnlyRole for the signed-in user, or for everyone once SHS-2026 is
 * closed. Prefer this in components over reading RoleReducer by hand, so the
 * rule stays in one place.
 */
export const useIsReadOnly = () => {
  const role = useSelector((state) => state.RoleReducer);
  return SHS_2026_CLOSED || isReadOnlyRole(role?.info?.role_id);
};

/**
 * The role-only half of useIsReadOnly, for screens that are not campaign data
 * entry (tweets) and so stay open after the campaign closes.
 */
export const useIsRoleReadOnly = () => {
  const role = useSelector((state) => state.RoleReducer);
  return isReadOnlyRole(role?.info?.role_id);
};

export const ROLE_READ_ONLY_NOTICE = 'Your role has view-only access and cannot submit this form.';

/**
 * useIsReadOnly for the CTU and CPS screens: they follow the campaign closure
 * only while CTU_CPS_OPEN is false, and view-only roles stay read-only always.
 */
export const useIsCtuCpsReadOnly = () => {
  const role = useSelector((state) => state.RoleReducer);
  return (SHS_2026_CLOSED && !CTU_CPS_OPEN) || isReadOnlyRole(role?.info?.role_id);
};

/** READ_ONLY_NOTICE for the CTU and CPS screens. */
export const CTU_CPS_READ_ONLY_NOTICE =
  SHS_2026_CLOSED && !CTU_CPS_OPEN ? CAMPAIGN_CLOSED_NOTICE : ROLE_READ_ONLY_NOTICE;

/**
 * GESI-sensitive Training Schedule (State level) stays open for uploads while
 * the rest of SHS-2026 is closed, the same way CTU_CPS_OPEN works. Set to false
 * to close it with everything else.
 */
export const TRAINING_SCHEDULE_OPEN = true;

/** useIsReadOnly for the Training Schedule screen. */
export const useIsTrainingScheduleReadOnly = () => {
  const role = useSelector((state) => state.RoleReducer);
  return (SHS_2026_CLOSED && !TRAINING_SCHEDULE_OPEN) || isReadOnlyRole(role?.info?.role_id);
};

/** READ_ONLY_NOTICE for the Training Schedule screen. */
export const TRAINING_SCHEDULE_READ_ONLY_NOTICE =
  SHS_2026_CLOSED && !TRAINING_SCHEDULE_OPEN ? CAMPAIGN_CLOSED_NOTICE : ROLE_READ_ONLY_NOTICE;

/**
 * Message shown next to a submit button that this user cannot use. Kept here
 * so every screen says the same thing.
 */
export const READ_ONLY_NOTICE = SHS_2026_CLOSED ? CAMPAIGN_CLOSED_NOTICE : ROLE_READ_ONLY_NOTICE;
